import { Trans } from "@lingui/react/macro";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import type { TOTPEnrollBeginResponse } from "../../../api";

import { ErrorCode } from "../../../api";
import {
  ApiConnector,
  apiErrorCode,
  RequestError,
} from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { Spinner } from "../../../components/ui-kit/spinner";
import { BackupCodes } from "./backup-codes";
import { useCodeErrorText } from "./code-error";
import { TotpEnrollment } from "./totp-enrollment";

type Step =
  | { kind: "loading" }
  | { kind: "totp"; enrollment: TOTPEnrollBeginResponse }
  | { kind: "backupCodes"; codes: string[] }
  | { kind: "expired" }
  /** Enrollment already went through — a first attempt whose reply was lost. */
  | { kind: "alreadyEnrolled" }
  /** Too many wrong codes — the backend stopped taking them. */
  | { kind: "exhausted" }
  /** The account is exempt from two-factor and may not enroll a method. */
  | { kind: "unavailable" }
  | { kind: "failed"; error: unknown };

/**
 * Setting up TOTP, in both places it happens: mandatory enrollment at login
 * (with a `challengeId`, since there is no session yet) and a voluntary opt-in
 * from account settings (without one). The steps are the same; the wording and
 * what finishing means come from the caller.
 */
export function MfaEnrollment({
  api,
  challengeId,
  intro,
  expiredMessage,
  onEnrolled,
  onCancel,
  cancelLabel,
}: {
  api: ApiConnector;
  challengeId?: string;
  intro: ReactNode;
  expiredMessage: ReactNode;
  onEnrolled: () => void;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const codeErrorText = useCodeErrorText();
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [codeError, setCodeError] = useState<unknown>();
  // Bumped to remount the setup screen, which is how a rejected code is cleared.
  const [attempt, setAttempt] = useState(0);

  const startEnrollment = useCallback(async () => {
    try {
      const enrollment = await api.startTotpEnrollment(challengeId);
      setStep({ kind: "totp", enrollment });
    } catch (error) {
      if (endedTheEnrollment(error)) {
        return;
      }

      // A fault, not a dead challenge — so keep the retry in reach.
      maybeReportError(error);
      setStep({ kind: "failed", error });
    }
  }, [api, challengeId]);

  // Enrollment starts right away: the challenge is only valid for a few
  // minutes, so a "get started" screen would just eat into that.
  useEffect(() => {
    void startEnrollment();
  }, [startEnrollment]);

  async function confirm(secret: string, code: string) {
    setCodeError(undefined);

    try {
      const { backup_codes } = await api.confirmTotpEnrollment(
        challengeId,
        secret,
        code,
      );

      if (backup_codes.length > 0) {
        setStep({ kind: "backupCodes", codes: backup_codes });
      } else {
        // No codes to show — the user still holds the ones from an earlier
        // enrollment, so there is nothing left to do here.
        onEnrolled();
      }
    } catch (error) {
      maybeReportError(error);

      // A rejected code leaves the secret bound, so the QR code still stands.
      // Only the code is cleared — a TOTP code is good for one time step.
      if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
        setAttempt((previous) => previous + 1);
        setCodeError(error);
        return;
      }

      if (endedTheEnrollment(error)) {
        return;
      }

      setCodeError(error);
    }
  }

  /**
   * Move a failure that ends the enrollment onto the step that says so.
   *
   * @returns whether the enrollment is over, leaving nothing to retry.
   */
  function endedTheEnrollment(error: unknown) {
    switch (apiErrorCode(error)) {
      // The attempt budget is spent. It is kept per account as well as per
      // challenge, so starting over won't hand out a fresh one either.
      case ErrorCode.RATE_LIMIT_EXCEEDED:
        setStep({ kind: "exhausted" });
        return true;

      // Exempt accounts are refused outright. `/mfa/status` doesn't say which
      // they are, so the offer to set two-factor up is made before we can know.
      case ErrorCode.PERMISSION_DENIED:
        setStep({ kind: "unavailable" });
        return true;

      // The account already holds a method, so a confirm that looked like it
      // failed had in fact gone through. Only reachable at login.
      case ErrorCode.TOKEN_ALREADY_USED:
        setStep({ kind: "alreadyEnrolled" });
        return true;
    }

    // Any other 401 means the binding is gone — the secret lapsed, or the
    // challenge did. Either way nothing on screen can still be confirmed.
    if (error instanceof RequestError && error.response.status === 401) {
      setStep({ kind: "expired" });
      return true;
    }

    return false;
  }

  /** Why the enrollment is over. Only the expired wording is the caller's. */
  function endMessage(
    kind: "expired" | "alreadyEnrolled" | "exhausted" | "unavailable",
  ) {
    switch (kind) {
      case "expired":
        return expiredMessage;

      case "alreadyEnrolled":
        return (
          <Trans>
            Two-factor authentication is already set up on this account. Please
            log in.
          </Trans>
        );

      case "exhausted":
        return (
          <Trans>
            Too many incorrect codes. Please wait a few minutes and start again.
          </Trans>
        );

      case "unavailable":
        return (
          <Trans>
            Two-factor authentication is not available for this account.
          </Trans>
        );
    }
  }

  switch (step.kind) {
    case "loading":
      return (
        <div className="flex justify-center">
          <Spinner />
        </div>
      );

    case "totp":
      return (
        <TotpEnrollment
          key={attempt}
          enrollment={step.enrollment}
          intro={intro}
          errorMessage={codeError ? codeErrorText(codeError) : ""}
          onConfirm={(code) => confirm(step.enrollment.secret, code)}
          onCancel={onCancel}
          cancelLabel={cancelLabel}
        />
      );

    case "backupCodes":
      return <BackupCodes codes={step.codes} onContinue={onEnrolled} />;

    case "failed":
      return (
        <div className="flex flex-col items-center">
          <ErrorMessage message={errorText(step.error)} />
          <div className="flex gap-2 mt-4">
            <Button onClick={startEnrollment}>
              <Trans>Try again</Trans>
            </Button>
            <Button variant="subtle" size="default" onClick={onCancel}>
              {cancelLabel}
            </Button>
          </div>
        </div>
      );

    case "expired":
    case "alreadyEnrolled":
    case "exhausted":
    case "unavailable":
      return (
        <div className="flex flex-col items-center">
          <ErrorMessage message={endMessage(step.kind)} />
          <Button className="mt-4" onClick={onCancel}>
            {cancelLabel}
          </Button>
        </div>
      );
  }
}
